import { FC, ReactNode, useMemo } from "react";
import { Doc } from "sharedb/lib/client";

import { INSTRUCTOR_SHARED_ACCESS_ID } from "../../../shared/access-id";
import { BlockType, WorksheetType } from "../../../shared/types/worksheet";
import { DATA_PATH } from "../../../shared/worksheets/constants";

import { showChangeModal } from "./ChangeModal";

import { DocViewContext, DocViewContextType } from "providers/DocViewProvider";
import { getFakeDoc } from "worksheets/helpers/fake-doc";
import { Blocks } from "worksheets/views/Blocks";

/** Must match a real Page in Redux or `Blocks` returns null (no worksheet preview). */
const blockDiffDocViewBase: Omit<DocViewContextType, "pageId"> = {
  readOnly: true,
  canInteractAsStudent: false,
  canAuthorPage: true,
  isAuthoringPage: false,
  canWriteDoc: true,
  showResponses: false,
  isProjecting: false,
  isFeaturing: false,
  isReleased: false,
  isStaticContent: false,
  selectedPageTabVariant: null,
  hideStaticBlocks: false,
  accessId: INSTRUCTOR_SHARED_ACCESS_ID,
  isDone: false,
};

// eslint-disable-next-line react-refresh/only-export-components
const BlockDiffWrapper: FC<{ pageId: string; children: ReactNode }> = ({ pageId, children }) => {
  const value = useMemo(() => ({ ...blockDiffDocViewBase, pageId }), [pageId]);
  return <DocViewContext.Provider value={value}>{children}</DocViewContext.Provider>;
};

/**
 * This Modal allows comparing changes to blocks on a page,
 * used to allow the user to accept or reject changes made by AI.
 *
 * TODO: This is mostly unstyled right now, but we could add per-block diff
 * view functionality here.
 */
export const showBlockDiffModal = (
  prevDoc: Doc<WorksheetType>,
  newBlocks: BlockType[],
  pageId: string,
) => {
  const newFakeDoc: Doc<WorksheetType> = getFakeDoc({
    b: newBlocks,
    d: prevDoc.data.d,
  });
  const CurrentWorksheet = (
    <Blocks accessIdPath={[DATA_PATH, INSTRUCTOR_SHARED_ACCESS_ID]} doc={prevDoc} />
  );
  const GeneratedWorksheet = (
    <Blocks accessIdPath={[DATA_PATH, INSTRUCTOR_SHARED_ACCESS_ID]} doc={newFakeDoc} />
  );
  const Wrapper: FC<{ children: ReactNode }> = ({ children }) => (
    <BlockDiffWrapper pageId={pageId}>{children}</BlockDiffWrapper>
  );
  return showChangeModal(CurrentWorksheet, GeneratedWorksheet, Wrapper);
};
